import { Injectable } from '@angular/core';
import { select } from '@ngneat/elf';
import { ppmStateStore, PpmStateProps, ppmInitialState } from './ppm-state.store';
import { BaseStoreRepository } from '../base-store.repository';
import { PpmMilestoneDto, Ppm, PpmDto, SlotDto, FundingDto } from '@/types';
import { format } from 'date-fns';
import { form } from '@/utils/form.utils';

@Injectable({
  providedIn: 'root',
})
export class PpmRepository extends BaseStoreRepository<PpmStateProps> {
  constructor() {
    super(ppmStateStore.pipe(select(state => state)), ppmStateStore, ppmInitialState);
  }

  ppmFields: (keyof PpmDto)[] = ['nom', 'acId', 'annee', 'comId', 'remarque'];

  milestoneFields: (keyof PpmMilestoneDto)[] = [
    'realisationEnvisagees',
    'beneficiaire',
    'typeMarcher.id',
    'modePassation.id',
    'budgetEstime',
    'comment',
  ];

  milestoneDates: (keyof PpmMilestoneDto)[] = ['datePrevLps', 'datePrevAc', 'datePrevDp', 'datePrevAp'];

  setResponseAndDto(response: Ppm) {
    this.set('response', response);
    this.set('dto', {
      ref: response.ref,
      nom: response.nom,
      acId: response.ac.id,
      annee: response.annee,
      ppmFiles: [],
      docIds: response.docs.map(doc => doc.id),
      remarque: response.remarque,
    });
    this.set('initialDto', structuredClone(this.get('dto')));
    this.set('acsSelected', { name: response.ac.nom, code: response.ac.id });
  }

  getCreatePayload(milestoneDtos: PpmMilestoneDto[]) {
    const data: Record<string, any> = {};
    const formData = new FormData();

    // Append global fields if available
    this.ppmFields.forEach(field => {
      const value = this.getNested('dto', field);
      if (value !== undefined && value !== null) {
        formData.append(field, String(value));
        data[field] = value;
      }
    });

    const realisationsLotsJson: Record<string, SlotDto[]> = {};
    const realisationsFinancementsJson: Record<string, FundingDto[]> = {};

    // Append milestone-related fields
    milestoneDtos.forEach((milestone, index) => {
      this.milestoneFields.forEach(field => {
        const value = milestone[field];
        if (form.isDefined(value)) {
          formData.append(`realisations[${index}].${field}`, String(value));
        }
      });
      this.milestoneDates.forEach(field => {
        const value = milestone[field]?.toString();
        if (value) formData.append(`realisations[${index}].${field}`, format(new Date(value), 'yyyy-MM-dd'));
      });
      milestone.realisationFiles.forEach(file => {
        formData.append(`realisations[${index}].realisationFiles`, file);
      });
      realisationsLotsJson[index.toString()] = milestone.lots;
      realisationsFinancementsJson[index.toString()] = milestone.financements;
    });

    // Append lots
    formData.append('realisationsLotsJson', JSON.stringify(realisationsLotsJson));

    // Append financements
    formData.append('realisationsFinancementsJson', JSON.stringify(realisationsFinancementsJson));

    // Append files
    this.getNested<File[]>('dto', 'ppmFiles')?.forEach(file => {
      formData.append('ppmFiles', file);
    });

    return { data, formData };
  }

  getUpdatePayload(milestoneDtos: PpmMilestoneDto[], prefix = '') {
    const data: Record<string, any> = {};
    const formData = new FormData();

    // Append global fields if available
    this.ppmFields.forEach(field => {
      const value = this.getNested('dto', field);
      if (form.isDefined(value)) {
        formData.append(`${prefix}${field}`, String(value));
        data[field] = value;
      }
    });

    const realisationsLotsJson: Record<string, SlotDto[]> = {};
    const realisationsFinancementsJson: Record<string, FundingDto[]> = {};

    milestoneDtos.forEach((milestone, index) => {
      if (milestone.id) {
        formData.append(`${prefix}realisations[${index}].id`, String(milestone.id));
        formData.append(`${prefix}realisations[${index}].docIds`, milestone.docIds.join(','));
      }
      this.milestoneFields.forEach(field => {
        const value = milestone[field];
        if (form.isDefined(value)) formData.append(`${prefix}realisations[${index}].${field}`, String(value));
      });
      this.milestoneDates.forEach(field => {
        const value = milestone[field]?.toString();
        if (value) formData.append(`${prefix}realisations[${index}].${field}`, format(new Date(value), 'yyyy-MM-dd'));
      });
      milestone.realisationFiles?.forEach(file => {
        formData.append(`${prefix}realisations[${index}].realisationFiles`, file);
      });
      realisationsFinancementsJson[index.toString()] = milestone.financements.map(financement => {
        //this block has to be optimized
        if (financement.id)
          return {
            id: financement.id,
            sourceFinancement: financement.sourceFinancement,
            budgetEstimer: financement.budgetEstimer,
            monnaieId: financement.monnaieId,
            examenPrealable: financement.examenPrealable,
          } satisfies FundingDto;
        else
          return {
            sourceFinancement: financement.sourceFinancement,
            budgetEstimer: financement.budgetEstimer,
            monnaieId: financement.monnaieId,
            examenPrealable: financement.examenPrealable,
          };
      });
      realisationsLotsJson[index.toString()] = milestone.lots.map(lot => {
        if (lot.id) return { id: lot.id, description: lot.description, budget: lot.budget };
        else return { description: lot.description, budget: lot.budget };
      });
    });

    // Append files
    this.getNested<File[]>('dto', 'ppmFiles').forEach(file => {
      formData.append(`${prefix}ppmFiles`, file);
    });
    formData.append(`${prefix}docIds`, this.getNested<number[]>('dto', 'docIds').join(','));

    // Append financements
    formData.append(`realisationsFinancementsJson`, JSON.stringify(realisationsFinancementsJson));

    // Append lots
    formData.append(`realisationsLotsJson`, JSON.stringify(realisationsLotsJson));
    return { data, formData };
  }
}
